import {ActionSheetOptions, alertController, AlertOptions, toastController, ToastOptions} from "@ionic/vue";
import {bugOutline, checkmarkOutline, cloudDownloadOutline} from "ionicons/icons";
import {DismissButton} from "@/models/ToastButtons/DismissButton";
import {UpdateButton} from "@/models/ToastButtons/UpdateButton";
import Translator from "@/plugins/Translator";
import {CancelButton} from "@/models/AlertButtons/CancelButton";
import {DeleteButton} from "@/models/AlertButtons/DeleteButton";
import {ConfirmButton} from "@/models/AlertButtons/ConfirmButton";

/**
 * Globale Benachrichtigungsklasse.
 * Stellt generische Methoden zur Verfügung, um Meldungen darzustellen.
 */
class Notifier {
    private static getPosition(options: ToastOptions) {
        return options.position ? options.position : "bottom";
    }

    private static getCssClass(options: ToastOptions) {
        const cssClass = "notifier ";
        if (options.cssClass != undefined) return cssClass + options.cssClass;
        if (options.position == "bottom" || options.position == undefined) return cssClass + "bottom-toast";
        if (options.position == "middle") return cssClass + "middle-toast";
        return "";
    }

    public static async showSuccess(options: ToastOptions) {
        const toast = await toastController.create({
            message: options.message,
            duration: options.duration ? options.duration : 3500,
            color: options.color ? options.color : undefined,
            icon: options.icon ? options.icon : checkmarkOutline,
            position: Notifier.getPosition(options),
            cssClass: Notifier.getCssClass(options),
        });
        await toast.present();
    }

    public static async showError(options: ToastOptions) {
        const toast = await toastController.create({
            message: options.message,
            duration: options.duration ? options.duration : 6000,
            color: options.color ? options.color : undefined,
            icon: options.icon ? options.icon : bugOutline,
            position: Notifier.getPosition(options),
            cssClass: Notifier.getCssClass(options),
        });
        await toast.present();
    }

    public static async showDismissibleError(options: ToastOptions) {
        const toast = await toastController.create({
            message: options.message,
            duration: options.duration ? options.duration : 15000,
            icon: options.icon ? options.icon : bugOutline,
            position: Notifier.getPosition(options),
            cssClass: Notifier.getCssClass(options),
            buttons: [DismissButton],
        });
        await toast.present();
    }

    public static async useAppUpdateToast() {
        return await toastController.create({
            message: "Update verfügbar",
            icon: cloudDownloadOutline,
            position: "top",
            duration: 0,
            cssClass: "update-toast",
            buttons: [UpdateButton],
        });
    }

    public static async getConfirmationAlert(options?: AlertOptions): Promise<HTMLIonAlertElement> {

        const translator = Translator.getInstance();
        const i18n = translator.getI18nGlobalInstance();

        const header = options?.header ?? i18n.t("alerts.delete.title");
        const message = options?.message ?? i18n.t("alerts.delete.message");
        const buttons = options?.buttons ?? [CancelButton, ConfirmButton];
        const alert = await alertController.create({
            header: header,
            message: message,
            buttons: buttons,
            backdropDismiss: false
        });

        return alert;
    }

    public static async getDeleteConfirmationAlert(options?: AlertOptions): Promise<HTMLIonAlertElement> {

        const translator = Translator.getInstance();
        const i18n = translator.getI18nGlobalInstance();

        const header = options?.header ?? i18n.t("alerts.delete.title");
        const message = options?.message ?? i18n.t("alerts.delete.message");
        const buttons = options?.buttons ?? [CancelButton, DeleteButton];
        const alert = await alertController.create({
            header: header,
            message: message,
            buttons: buttons
        });

        return alert;
    }
}

export default Notifier;
